import { api } from "@/client/api";
import { configs } from "@/configs";
import {
  PaymentMethodCode,
  Product,
  ProductInput,
  ProductMembership,
  ProductMembershipInput,
  ProductPT,
  ProductPTInput,
} from "@/models";
import { parseDateTimeFromPrisma } from "@/utils";

type GetProductMemberships = {
  data: ProductMembership[];
  total: number;
};

type GetProductPTs = {
  data: ProductPT[];
  total: number;
};

type GetProducts = {
  data: Product[];
  total: number;
};

type PublishProductInput = {
  id: number;
  isPublished: boolean;
};

type PurchaseProductsInput = {
  memberId?: number;
  staffId: number;
  purchaseProducts: {
    productId: number;
    name: string;
    price: number;
    quantity: number;
    discountBaht: number;
    discountPercentage: number;
  }[];
  paymentMethodCode: PaymentMethodCode;
  bankId?: number;
  isInstallment?: boolean;
  installmentMonths?: number;
  interestRate?: number;
};

type UpdateProductInput = Partial<ProductInput> & { id: number };

const productMembershipPath = "/products/memberships";
const productPTPath = "/products/pts";
const productPath = "/products";

export async function getProductMemberships(query = "") {
  const { data } = await api.get<GetProductMemberships>(
    `${configs.apiUrl}${productMembershipPath}?${query}`
  );
  return data;
}

export async function getProductMembership(id: string) {
  const { data } = await api.get<ProductMembership>(
    `${configs.apiUrl}${productMembershipPath}/${id}`
  );
  return data;
}

export async function createProductMembership(body: ProductMembershipInput) {
  const { data } = await api.post<ProductMembership>(
    `${configs.apiUrl}${productMembershipPath}`,
    body
  );
  return data;
}

export async function deleteProductMembership(id: string) {
  const { data } = await api.delete<ProductMembership>(
    `${configs.apiUrl}${productMembershipPath}/${id}`
  );
  return data;
}

export async function getProductPTs(query = "") {
  const { data } = await api.get<GetProductPTs>(
    `${configs.apiUrl}${productPTPath}?${query}`
  );
  return data;
}

export async function getProductPT(id: string) {
  const { data } = await api.get<ProductPT>(
    `${configs.apiUrl}${productPTPath}/${id}`
  );
  return data;
}

export async function createProductPT(body: ProductPTInput) {
  const { data } = await api.post<ProductPT>(
    `${configs.apiUrl}${productPTPath}`,
    body
  );
  return data;
}

export async function deleteProductPT(id: string) {
  const { data } = await api.delete<ProductPT>(
    `${configs.apiUrl}${productPTPath}/${id}`
  );
  return data;
}

export async function getProducts(query = "") {
  const { data } = await api.get<GetProducts>(
    `${configs.apiUrl}${productPath}?${query}`
  );
  return parseDateTimeFromPrisma(data) as GetProducts;
}

export async function getOpenProducts() {
  const { data } = await api.get<GetProducts>(
    `${configs.apiUrl}${productPath}?isPublished=true&limit=300`
  );
  return parseDateTimeFromPrisma(data) as GetProducts;
}

export async function getProduct(id: string) {
  const { data } = await api.get<Product>(
    `${configs.apiUrl}${productPath}/${id}`
  );
  return parseDateTimeFromPrisma(data) as Product;
}

export async function publishProduct(
  input: PublishProductInput
): Promise<void> {
  const { id, isPublished } = input;
  await api.patch(`${configs.apiUrl}/products/${id}/publish`, {
    isPublished,
  });
}

export async function deleteProduct(id: string) {
  const { data } = await api.delete<Product>(
    `${configs.apiUrl}${productPath}/${id}`
  );
  return data;
}

export async function createProduct(body: ProductInput) {
  const { data } = await api.post<Product>(
    `${configs.apiUrl}${productPath}`,
    body
  );
  return data;
}

export async function updateProduct({
  id,
  ...input
}: UpdateProductInput): Promise<Product> {
  const { data } = await api.patch<Product>(
    `${configs.apiUrl}${productPath}/${id}`,
    input
  );

  return data;
}

export async function purchaseProducts(input: PurchaseProductsInput) {
  await api.post(`${configs.apiUrl}${productPath}/purchase`, input);
}
